/**
 * `ProtectedLayout` Component
 *
 * This component serves as a protected layout wrapper for authenticated users.
 * It ensures that only logged-in users can access the application's main content.
 *
 * - If the user is not logged in, they are redirected to the login page.
 * - If the user's code is `"PW01"`, they are redirected to the change-password page.
 * - Manages sidebar state for navigation.
 * - Displays the franchise name, fetched from session storage.
 *
 * @returns {JSX.Element} The protected layout structure with sidebar, header, and dynamic content.
 */
import React, { createContext, useContext, useState } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { AppSessionService } from "../../infrastructure/services/session"

// Create Context for Search
const SearchContext = createContext<{
  searchQuery: string
  setSearchQuery: (query: string) => void
}>({
  searchQuery: "",
  setSearchQuery: () => { },
})
export const useSearch = () => useContext(SearchContext)

const ProtectedLayout: React.FC = () => {
  const { isLoggedIn, userCode } = useAuth()
  const location = useLocation()

  if (userCode === "PW01" && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" />
  }

  if (!isLoggedIn && location.pathname !== "/change-password") {
    return <Navigate to="/login" replace />
  }

  return (
    <Outlet />
  )
}

export default ProtectedLayout